import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { AuthActions } from 'state/modules/auth';
import { FETCH_STATE } from 'constants/common';
import { Button, Box, Grid, Typography, List, ListItemText, ListItem, Card } from '@mui/material';

import {
  ChangeEmailModal,
  ChangePhoneModal,
  ChangeAccountPassword,
  ChangeAccountInfoModal,
} from 'components/Modals';

const AccountInfo = () => {
  const dispatch = useDispatch();

  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [openPhoneModal, setOpenPhoneModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [firstStatingTwoFactor, setFirstStatingTwoFactor] = useState(true);

  const { user, startingTwoFactor } = useSelector((state) => state.auth);

  const handleOpenInfoModal = () => setOpenInfoModal((prevValue) => !prevValue);
  const handleOpenEmailModal = () => setOpenEmailModal((prevValue) => !prevValue);
  const handleOpenPasswordModal = () => setOpenPasswordModal((prevValue) => !prevValue);
  const handleOpenPhoneModal = () => {
    dispatch(AuthActions.resetVerification());
    setOpenPhoneModal((prevValue) => !prevValue);
  };

  useEffect(() => {
    if (!user?.ciam_id) {
      setFirstStatingTwoFactor(false);
    }
  }, [user]);

  useEffect(() => {
    if (startingTwoFactor === FETCH_STATE.DONE || startingTwoFactor === FETCH_STATE.ERROR) {
      setFirstStatingTwoFactor(false);
    }
  }, [startingTwoFactor]);

  useEffect(() => {
    if (!openPasswordModal && user?.ciam_id) {
      setFirstStatingTwoFactor(true);
    }
  }, [openPasswordModal, user]);

  /**
   * Parse user personal data
   * @param {object} user Object with user data
   */
  const getPersonalInfo = (user = {}) => {
    const userInfo = [
      { label: 'Name:', value: user?.first_name || '-' },
      { label: 'Middle Name:', value: user?.middle_name || '-' },
      { label: 'Last Name:', value: user?.last_name || '-' },
    ];

    return userInfo;
  };

  /**
   * Parse user contact data
   * @param {object} user Object with user data
   */
  const getContactInfo = (user = {}) => {
    const userInfo = [
      {
        name: 'email',
        label: 'Email:',
        value: user?.email || '-',
        buttonText: i18n.t('account.change_email'),
        onClick: handleOpenEmailModal,
      },
      {
        name: 'phone',
        label: 'Phone:',
        value: user?.mobile_phone || '-',
        buttonText: i18n.t('account.change_phone'),
        onClick: handleOpenPhoneModal,
      },
    ];

    return userInfo;
  };

  return (
    <Card data-cy="account-info">
      <Box sx={{ p: '24px' }}>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12 }}>
            <Box mt={2} mb={2} display="flex" justifyContent="space-between">
              <Typography variant="h6">
                <Box
                  fontWeight="fontWeightBold"
                  component="span"
                  display="inline-block"
                  fontSize={16}
                >
                  {i18n.t('account.personal_info')}
                </Box>
              </Typography>
              <Box>
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  disableFocusRipple={true}
                  data-cy="change-user-info"
                  onClick={handleOpenInfoModal}
                  sx={(theme) => ({
                    p: 0,
                    textTransform: 'capitalize',
                    fontSize: theme.typography.pxToRem(13),
                  })}
                >
                  {i18n.t('account.update_info')}
                </Button>
              </Box>
            </Box>
            <List>
              {getPersonalInfo(user).map((val, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: 'inline-block',
                    padding: '0 0 8px 0',
                  }}
                >
                  <ListItemText
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                    primary={
                      <Box component="div" display="flex" alignItems="center">
                        <Typography
                          sx={(theme) => ({
                            color: theme.palette.secondary.dark,
                            fontWeight: 'bold',
                            fontSize: theme.typography.pxToRem(13),
                          })}
                        >
                          {val.label}
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            ml: '10px',
                            color: theme.palette.secondary.light,
                            fontSize: theme.typography.pxToRem(13),
                          })}
                        >
                          {val.value}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6">
              <Box
                fontWeight="fontWeightBold"
                fontSize={16}
                mb={2}
                mt={8}
                component="span"
                display="block"
              >
                {i18n.t('account.contact_info')}
              </Box>
            </Typography>
            <List>
              {getContactInfo(user).map((val, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: 'inline-block',
                    padding: '0 0 8px 0',
                  }}
                >
                  <ListItemText
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                    primary={
                      <Box component="div" display="flex" alignItems="center">
                        <Typography
                          sx={(theme) => ({
                            color: theme.palette.secondary.dark,
                            fontWeight: 'bold',
                            fontSize: theme.typography.pxToRem(13),
                          })}
                        >
                          {val.label}
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            ml: '10px',
                            color: theme.palette.secondary.light,
                            fontSize: theme.typography.pxToRem(13),
                          })}
                        >
                          {val.value}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Button
                        type="button"
                        variant="text"
                        color="primary"
                        disableFocusRipple={true}
                        data-cy={`change-user-${val.name}`}
                        onClick={val.onClick}
                        sx={(theme) => ({
                          p: 0,
                          textTransform: 'capitalize',
                          fontSize: theme.typography.pxToRem(13),
                        })}
                      >
                        {val.buttonText || '-'}
                      </Button>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box mt={8} display="flex" justifyContent="space-between">
              <Typography variant="h6">
                <Box
                  fontWeight="fontWeightBold"
                  component="span"
                  display="inline-block"
                  fontSize={16}
                >
                  {i18n.t('account.password')}
                </Box>
              </Typography>
              <Box>
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  disableFocusRipple={true}
                  data-cy="change-user-password"
                  onClick={handleOpenPasswordModal}
                  sx={(theme) => ({
                    p: 0,
                    textTransform: 'capitalize',
                    fontSize: theme.typography.pxToRem(13),
                  })}
                  disabled={startingTwoFactor == FETCH_STATE.LOADING}
                >
                  {i18n.t('account.change_password')}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {openInfoModal && (
        <ChangeAccountInfoModal
          id={user.id}
          user={user}
          open={openInfoModal}
          handleClick={handleOpenInfoModal}
        />
      )}
      {openEmailModal && (
        <ChangeEmailModal user={user} open={openEmailModal} handleClick={handleOpenEmailModal} />
      )}
      {openPhoneModal && (
        <ChangePhoneModal user={user} open={openPhoneModal} handleClick={handleOpenPhoneModal} />
      )}
      {openPasswordModal && (
        <ChangeAccountPassword
          user={user}
          open={openPasswordModal && firstStatingTwoFactor === false}
          handleClick={handleOpenPasswordModal}
        />
      )}
    </Card>
  );
};

AccountInfo.propTypes = {};

AccountInfo.defaultProps = {};

export default AccountInfo;
